import { useState, FormEvent, useEffect } from 'react';

const DONATE_URL = 'https://readdy.ai/api/form/d7i08s7hkiob2r09iqug';

const donationLevels = [
  {
    amount: 20000,
    label: '$20.000',
    title: 'Semilla',
    description: 'Financia materiales educativos de reciclaje para un estudiante durante un mes.',
    icon: 'ri-seedling-line',
    color: '#4caf7d',
  },
  {
    amount: 50000,
    label: '$50.000',
    title: 'Árbol',
    description: 'Apoya un taller de reciclaje consciente para 10 estudiantes en un colegio.',
    icon: 'ri-plant-line',
    color: '#2d8a5e',
    featured: true,
  },
  {
    amount: 100000,
    label: '$100.000',
    title: 'Bosque',
    description: 'Financia un punto de reciclaje completo en un parque o institución educativa.',
    icon: 'ri-leaf-line',
    color: '#1a3a2e',
  },
  {
    amount: 0,
    label: 'Otro valor',
    title: 'A tu medida',
    description: 'Elige el monto que desees. Cada peso hace la diferencia en nuestra misión.',
    icon: 'ri-heart-line',
    color: '#e8734a',
  },
];

const impacts = [
  { icon: 'ri-book-open-line', text: 'Materiales educativos para 5 estudiantes', amount: '$10.000' },
  { icon: 'ri-group-line', text: 'Taller comunitario de reciclaje', amount: '$30.000' },
  { icon: 'ri-recycle-line', text: 'Punto de reciclaje en un parque', amount: '$80.000' },
  { icon: 'ri-building-line', text: 'Programa completo en un colegio', amount: '$200.000' },
];

export default function DonatePage() {
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getAmount = () => {
    if (donationLevels[selectedLevel].amount === 0) return customAmount;
    return donationLevels[selectedLevel].amount.toString();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const amount = getAmount();
    if (!amount || Number(amount) <= 0) {
      setError('Por favor ingresa un monto válido.');
      return;
    }
    if (!name || !email) {
      setError('Por favor completa todos los campos requeridos.');
      return;
    }
    setLoading(true);
    try {
      const body = new URLSearchParams({
        name,
        email,
        monto: amount,
        nivel: donationLevels[selectedLevel].title,
        mensaje: message,
      });
      await fetch(DONATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Ocurrió un error. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative w-full min-h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=hands%20holding%20young%20green%20plant%20seedling%2C%20soil%20background%2C%20hope%20and%20growth%20concept%2C%20warm%20golden%20light%2C%20macro%20photography%2C%20environmental%20care%2C%20Colombia%2C%20high%20quality%20photography&width=1920&height=700&seq=donate001&orientation=landscape"
            alt="Donar a Fundación Frais"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent"></div>
        </div>
        <div className="relative z-10 w-full px-8 md:px-20 pb-20 pt-40">
          <p className="text-[#4caf7d] text-xs tracking-widest uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Haz la Diferencia
          </p>
          <h1
            className="text-5xl md:text-7xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Tu Donación<br />
            <em className="not-italic text-[#4caf7d]">Transforma Vidas</em>
          </h1>
        </div>
      </section>

      {/* Impact Preview */}
      <section className="bg-[#1a3a2e] py-10 px-8 md:px-20">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/20">
          {impacts.map((item) => (
            <div key={item.text} className="flex items-center gap-4 py-4 sm:py-0 px-8 sm:px-10">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#4caf7d]/20 flex-shrink-0">
                <i className={`${item.icon} text-[#4caf7d] text-lg`}></i>
              </div>
              <div>
                <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.amount}</p>
                <p className="text-white/60 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-24 md:py-32 px-8 md:px-20 bg-[#f7f5f2]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gray-400 text-xs tracking-widest uppercase mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Elige tu Nivel de Impacto
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              ¿Cuánto deseas donar?
            </h2>
          </div>

          {submitted ? (
            <div className="bg-white rounded-3xl p-16 text-center max-w-2xl mx-auto">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#d4f1e8] mx-auto mb-8">
                <i className="ri-checkbox-circle-line text-4xl text-[#4caf7d]"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1a3a2e] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                ¡Gracias por tu donación!
              </h3>
              <p className="text-gray-500 text-base leading-relaxed mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Tu generosidad hace posible que más comunidades accedan a programas de reciclaje consciente. Recibirás un correo de confirmación con los detalles de tu donación.
              </p>
              <div className="flex items-center justify-center gap-2 text-[#4caf7d]">
                <i className="ri-leaf-line text-xl"></i>
                <span className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Juntos estamos transformando Colombia
                </span>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="donation-form"
              className="bg-white rounded-3xl p-8 md:p-12"
            >
              {/* Donation Levels */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {donationLevels.map((level, i) => (
                  <button
                    key={level.title}
                    type="button"
                    onClick={() => setSelectedLevel(i)}
                    className={`relative rounded-2xl p-6 text-left transition-all duration-300 cursor-pointer border-2 ${
                      selectedLevel === i
                        ? 'border-[#4caf7d] bg-[#f0faf5]'
                        : 'border-gray-100 bg-[#faf8f5] hover:border-gray-200'
                    }`}
                  >
                    {level.featured && (
                      <span
                        className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#4caf7d] text-white text-xs font-semibold whitespace-nowrap"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      >
                        Más Popular
                      </span>
                    )}
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${level.color}15` }}>
                      <i className={`${level.icon} text-xl`} style={{ color: level.color }}></i>
                    </div>
                    <p className="text-xl font-bold text-[#1a3a2e] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {level.label}
                    </p>
                    <p className="text-[#4caf7d] text-xs font-semibold tracking-wide uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {level.title}
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {level.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              {donationLevels[selectedLevel].amount === 0 && (
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-[#1a3a2e] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Monto personalizado (COP)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                    <input
                      type="number"
                      name="monto_personalizado"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Ej: 75000"
                      min="1000"
                      className="w-full pl-8 pr-4 py-4 rounded-xl border border-gray-200 text-[#1a3a2e] text-base focus:outline-none focus:border-[#4caf7d] transition-colors"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    />
                  </div>
                </div>
              )}

              {/* Personal Info */}
              <div className="border-t border-gray-100 pt-8 mb-8">
                <p className="text-sm font-semibold text-[#1a3a2e] mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Tus datos de contacto
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Tu nombre"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#1a3a2e] text-sm focus:outline-none focus:border-[#4caf7d] transition-colors"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#1a3a2e] text-sm focus:outline-none focus:border-[#4caf7d] transition-colors"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2 uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Mensaje (opcional)
                  </label>
                  <textarea
                    name="mensaje"
                    value={message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setMessage(e.target.value);
                    }}
                    placeholder="¿Por qué decides donar? Tu historia nos inspira..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#1a3a2e] text-sm focus:outline-none focus:border-[#4caf7d] transition-colors resize-none"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{message.length}/500</p>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm mb-6 bg-red-50 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  <span style={{ fontFamily: "'Montserrat', sans-serif" }}>{error}</span>
                </div>
              )}

              {/* Submit */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4 text-gray-400 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <div className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-[#4caf7d]"></i>
                    <span>Donación segura</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-file-text-line text-[#4caf7d]"></i>
                    <span>Certificado de donación</span>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-3 px-10 py-4 rounded-full bg-[#1a3a2e] text-white font-semibold text-sm tracking-wider uppercase hover:bg-[#2d5a42] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-heart-fill text-[#4caf7d]"></i>
                  {loading ? 'Procesando...' : `Donar ${donationLevels[selectedLevel].amount === 0 ? (customAmount ? `$${customAmount}` : '') : donationLevels[selectedLevel].label}`}
                  {!loading && <i className="ri-arrow-right-line"></i>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Bank Accounts */}
      <section className="py-16 bg-[#f0faf5] px-8 md:px-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-xs tracking-widest uppercase mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Transferencia Bancaria
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Donar directamente a nuestra cuenta
            </h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Si prefieres hacer tu donación por transferencia directa, usa cualquiera de estas cuentas.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Bancolombia */}
            <div className="bg-white rounded-2xl p-8 flex flex-col gap-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fff3e0' }}>
                  <i className="ri-bank-fill text-2xl" style={{ color: '#f57f17' }}></i>
                </div>
                <div>
                  <p className="text-[#1a3a2e] font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Bancolombia</p>
                  <p className="text-gray-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>Cuenta de Ahorros</p>
                </div>
              </div>
              <div className="bg-[#fafaf8] rounded-xl p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Número de cuenta</p>
                <p className="text-[#1a3a2e] font-bold text-xl tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>814 61965440</p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Titular</span>
                  <span className="text-[#1a3a2e] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Fundación Frais</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>NIT</span>
                  <span className="text-[#1a3a2e] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>901846693-3</span>
                </div>
              </div>
            </div>

            {/* Nequi 1 */}
            <div className="bg-white rounded-2xl p-8 flex flex-col gap-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fce4ec' }}>
                  <i className="ri-smartphone-fill text-2xl" style={{ color: '#e91e63' }}></i>
                </div>
                <div>
                  <p className="text-[#1a3a2e] font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Nequi</p>
                  <p className="text-gray-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>Cuenta #1</p>
                </div>
              </div>
              <div className="bg-[#fafaf8] rounded-xl p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Número Nequi</p>
                <p className="text-[#1a3a2e] font-bold text-xl tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>319 336 1911</p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Titular</span>
                  <span className="text-[#1a3a2e] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Fundación Frais</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Confirmar</span>
                  <a href="mailto:Fundacion.frais@gmail.com" className="text-[#4caf7d] font-semibold cursor-pointer hover:underline text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>Fundacion.frais@gmail.com</a>
                </div>
              </div>
            </div>

            {/* Nequi 2 */}
            <div className="bg-white rounded-2xl p-8 flex flex-col gap-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fce4ec' }}>
                  <i className="ri-smartphone-fill text-2xl" style={{ color: '#e91e63' }}></i>
                </div>
                <div>
                  <p className="text-[#1a3a2e] font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Nequi</p>
                  <p className="text-gray-400 text-xs uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>Cuenta #2</p>
                </div>
              </div>
              <div className="bg-[#fafaf8] rounded-xl p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Número Nequi</p>
                <p className="text-[#1a3a2e] font-bold text-xl tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>304 213 3550</p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Titular</span>
                  <span className="text-[#1a3a2e] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Fundación Frais</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Confirmar</span>
                  <a href="mailto:Fundacion.frais@gmail.com" className="text-[#4caf7d] font-semibold cursor-pointer hover:underline text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>Fundacion.frais@gmail.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Donate */}
      <section className="py-24 bg-white px-8 md:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gray-400 text-xs tracking-widest uppercase mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Transparencia Total
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              ¿A dónde va tu donación?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { pct: '70%', label: 'Programas Educativos', desc: 'Talleres, materiales y formación de líderes ambientales en colegios y universidades.', icon: 'ri-book-open-line' },
              { pct: '20%', label: 'Infraestructura', desc: 'Instalación y mantenimiento de puntos de reciclaje en parques, empresas e instituciones.', icon: 'ri-recycle-line' },
              { pct: '10%', label: 'Operación', desc: 'Gestión administrativa y comunicaciones para maximizar el impacto de cada peso donado.', icon: 'ri-settings-line' },
            ].map((item) => (
              <div key={item.label} className="text-center p-8 rounded-2xl bg-[#faf8f5]">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#d4f1e8] mx-auto mb-6">
                  <i className={`${item.icon} text-2xl text-[#1a3a2e]`}></i>
                </div>
                <p className="text-5xl font-bold text-[#4caf7d] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {item.pct}
                </p>
                <p className="text-[#1a3a2e] font-bold text-lg mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {item.label}
                </p>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
